import { useState } from "react";

const differentials = [
  {
    icon: "ri-user-heart-line",
    title: "Atendimento Personalizado",
    text: "Cada cliente é único. Dedicamos atenção integral a cada caso, com escuta ativa e comunicação clara em todas as etapas.",
  },
  {
    icon: "ri-lightbulb-flash-line",
    title: "Estratégia Jurídica Eficiente",
    text: "Desenvolvemos soluções jurídicas sob medida, combinando profundo conhecimento técnico com visão estratégica orientada a resultados.",
  },
  {
    icon: "ri-shield-star-line",
    title: "Segurança e Confiabilidade",
    text: "Transparência e ética como pilares fundamentais. O cliente acompanha cada passo com total clareza sobre o andamento do seu caso.",
  },
  {
    icon: "ri-award-line",
    title: "Atuação Técnica Comprometida",
    text: "Excelência jurídica baseada em estudo contínuo, atualização constante e aplicação rigorosa da legislação e jurisprudência vigentes.",
  },
  {
    icon: "ri-flag-2-line",
    title: "Compromisso com Resultados",
    text: "Trabalhamos incansavelmente para obter os melhores resultados possíveis, com responsabilidade e respeito aos limites éticos da profissão.",
  },
];

const VISIBLE = 3;
const MAX_INDEX = differentials.length - VISIBLE;

export default function Diferenciais() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [mobileCurrent, setMobileCurrent] = useState(0);

  const prev = () => setCurrentIndex((i) => Math.max(0, i - 1));
  const next = () => setCurrentIndex((i) => Math.min(MAX_INDEX, i + 1));

  const mobilePrev = () => setMobileCurrent((i) => Math.max(0, i - 1));
  const mobileNext = () => setMobileCurrent((i) => Math.min(differentials.length - 1, i + 1));

  return (
    <section id="diferenciais" className="bg-white py-24">
      <div className="max-w-7xl mx-auto px-6">

        {/* Top heading */}
        <div className="mb-14">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-px bg-gold-medium" />
            <span className="text-gold-medium text-xs font-semibold tracking-widest uppercase">
              Diferenciais
            </span>
          </div>
          <div className="grid lg:grid-cols-2 gap-10 items-end">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-petrol-dark leading-tight">
                Por que escolher o <br />
                <span className="text-gold-medium">Georges Humbert</span>
              </h2>
            </div>
            <div>
              <div className="border-l-4 border-gold-light/60 pl-5 py-2">
                <p className="text-petrol-dark/70 text-sm italic leading-relaxed">
                  "A advocacia é uma missão que exige técnica, ética e total
                  comprometimento com quem nos confia sua causa."
                </p>
                <p className="text-gold-medium text-xs font-semibold mt-2 tracking-wide">
                  Georges Humbert, Advogado
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ── MOBILE CAROUSEL (1 card at a time) ── */}
        <div className="block md:hidden">
          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${mobileCurrent * 100}%)` }}
            >
              {differentials.map((d) => (
                <div key={d.title} className="w-full flex-shrink-0 px-1">
                  <div className="group flex flex-col gap-4 p-7 h-full rounded-lg border border-silver/30 hover:border-gold-light/50 bg-ivory hover:bg-white transition-all duration-300">
                    <div className="w-12 h-12 flex items-center justify-center shrink-0 rounded-full bg-petrol-dark/6 group-hover:bg-petrol-dark/10 transition-colors duration-300">
                      <i className={`${d.icon} text-petrol-dark text-xl`} />
                    </div>
                    <div>
                      <h4 className="text-petrol-dark font-semibold text-base mb-2">{d.title}</h4>
                      <p className="text-petrol-dark/60 text-sm leading-relaxed">{d.text}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile navigation */}
          <div className="flex items-center justify-center gap-5 mt-8">
            <button
              onClick={mobilePrev}
              disabled={mobileCurrent === 0}
              className={`w-10 h-10 flex items-center justify-center rounded-full border transition-all duration-300 cursor-pointer ${
                mobileCurrent === 0
                  ? "border-silver/30 text-petrol-dark/20 cursor-not-allowed"
                  : "border-petrol-dark/30 text-petrol-dark hover:border-gold-medium hover:text-gold-medium"
              }`}
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-left-s-line text-lg" />
              </span>
            </button>

            <div className="flex items-center gap-2">
              {differentials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setMobileCurrent(i)}
                  className={`rounded-full transition-all duration-300 cursor-pointer ${
                    i === mobileCurrent
                      ? "w-6 h-2 bg-gold-medium"
                      : "w-2 h-2 bg-silver/50 hover:bg-gold-light/50"
                  }`}
                />
              ))}
            </div>

            <button
              onClick={mobileNext}
              disabled={mobileCurrent === differentials.length - 1}
              className={`w-10 h-10 flex items-center justify-center rounded-full border transition-all duration-300 cursor-pointer ${
                mobileCurrent === differentials.length - 1
                  ? "border-silver/30 text-petrol-dark/20 cursor-not-allowed"
                  : "border-petrol-dark/30 text-petrol-dark hover:border-gold-medium hover:text-gold-medium"
              }`}
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-lg" />
              </span>
            </button>
          </div>
        </div>

        {/* ── DESKTOP CAROUSEL (3 cards at a time) ── */}
        <div className="hidden md:block">
          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentIndex * (100 / VISIBLE)}%)` }}
            >
              {differentials.map((d) => (
                <div key={d.title} className="w-1/3 flex-shrink-0 px-3">
                  <div className="group flex flex-col gap-4 p-7 h-full rounded-lg border border-silver/30 hover:border-gold-light/50 bg-ivory hover:bg-white transition-all duration-300">
                    <div className="w-12 h-12 flex items-center justify-center shrink-0 rounded-full bg-petrol-dark/6 group-hover:bg-petrol-dark/10 transition-colors duration-300">
                      <i className={`${d.icon} text-petrol-dark text-xl`} />
                    </div>
                    <div>
                      <h4 className="text-petrol-dark font-semibold text-base mb-2">{d.title}</h4>
                      <p className="text-petrol-dark/60 text-sm leading-relaxed">{d.text}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Desktop navigation */}
          <div className="flex items-center justify-center gap-5 mt-10">
            <button
              onClick={prev}
              disabled={currentIndex === 0}
              className={`w-10 h-10 flex items-center justify-center rounded-full border transition-all duration-300 cursor-pointer ${
                currentIndex === 0
                  ? "border-silver/30 text-petrol-dark/20 cursor-not-allowed"
                  : "border-petrol-dark/30 text-petrol-dark hover:border-gold-medium hover:text-gold-medium"
              }`}
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-left-s-line text-lg" />
              </span>
            </button>

            <div className="flex items-center gap-2">
              {Array.from({ length: MAX_INDEX + 1 }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentIndex(i)}
                  className={`rounded-full transition-all duration-300 cursor-pointer ${
                    i === currentIndex
                      ? "w-6 h-2 bg-gold-medium"
                      : "w-2 h-2 bg-silver/50 hover:bg-gold-light/50"
                  }`}
                />
              ))}
            </div>

            <button
              onClick={next}
              disabled={currentIndex === MAX_INDEX}
              className={`w-10 h-10 flex items-center justify-center rounded-full border transition-all duration-300 cursor-pointer ${
                currentIndex === MAX_INDEX
                  ? "border-silver/30 text-petrol-dark/20 cursor-not-allowed"
                  : "border-petrol-dark/30 text-petrol-dark hover:border-gold-medium hover:text-gold-medium"
              }`}
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-lg" />
              </span>
            </button>
          </div>
        </div>

      </div>
    </section>
  );
}
